import { useCallback, useMemo, useRef } from "react";
import { getCurrentAuth } from "../../../hooks/useAxiosPrivate";
import { refreshAuthToken } from "../../../utils/authRefreshInterceptor";
import { toast } from "react-toastify";
import { canDownloadFrom, permissionsFromToken } from "../../../utils/audienceLayerEntitlement";

// Re-exported: this hook's own tests, and any other existing consumer of this
// module, import permissionsFromToken from here. The function itself now
// lives in src/utils/audienceLayerEntitlement.ts, where the Studio page (v2)
// can reach it too, since src/v2 cannot import from src/Pages.
export { permissionsFromToken };

/**
 * How wide a clip's captured frame is stored.
 *
 * The tile it fills is 112 pixels across, so this is enough for any display
 * worth the pixels and small enough that the capture rides along inside the
 * create request instead of needing an upload of its own.
 */
const THUMBNAIL_WIDTH = 320;

/**
 * The contract the Audience Layer remote expects. Declared here rather than
 * imported: the remote is resolved at runtime by Module Federation, so the
 * host has no compile-time module to import types from, and a copy that
 * drifts is caught by the remote's own runtime validation.
 */
export interface AudienceLayerHostContext {
  gameId: string;
  tenant: string;
  user: {
    id: string;
    name: string;
    avatarUrl?: string;
    jwt: string;
    isModerator?: boolean;
  };
  device: string;
  apiBaseUrl?: string;
  /**
   * A Moment the fan followed a link to, from the Studio's Clips tab.
   *
   * The layer opens its Moments panel on `tab` with `momentId` picked out.
   * Null on an ordinary visit, which is every visit that did not come from a
   * clip card.
   *
   * THE ID, NOT THE TITLE. The Watch page used to raise a banner naming the
   * clip, because the moments list beside it lists the game's key moments and
   * a clip a fan made is not one of them. Sending the id instead lets the
   * panel say it in the list the clip really lives in, and the banner is gone.
   *
   * `tab` is null when the link did not say, or said something this build does
   * not know: the panel opens on its own default rather than guessing, so a
   * link that outlives a rename still works.
   */
  clipTarget?: { momentId: string; tab: "mine" | "shared" | null } | null;
  /**
   * Whether this fan may download. A courtesy, not the gate: see the
   * useMemo that computes it below for why the tenant backend still runs
   * the real check on every request.
   */
  canDownload: boolean;
  /**
   * Say something to the fan, in this application's own voice.
   *
   * The layer is a 380px panel beside the video, and a message it renders
   * itself is a message inside that column. Anything worth interrupting
   * somebody for belongs where this app already puts them, which is the
   * app-global ToastContainer in App.tsx, top right, above everything.
   *
   * The layer decides WHAT to say and this decides WHERE it appears, which
   * keeps one notification style across a page the fan reads as one product
   * rather than two.
   */
  notify(message: string, kind?: "info" | "error"): void;
  getPlaybackPositionMs(): number;
  getGameClock(): string | null;
  /**
   * Playback control, for watch party sync. Everything above this is the
   * layer reading the page; this is the layer acting on it, and it exists
   * because a watch party is other people's play, pause and seek arriving
   * here and having to reach this player.
   *
   * Optional: the remote also runs standalone with no player at all, and
   * checks for this before offering anyone the controls.
   */
  player?: {
    getPositionMs(): number;
    isPlaying(): boolean;
    isStalled(): boolean;
    play(): void;
    pause(): void;
    seekMs(positionMs: number): void;
    // Sync corrects small drift by nudging the rate rather than seeking,
    // because a seek is visible and a 2% rate change is not. Only a drift too
    // large to close that way becomes a seek.
    getPlaybackRate(): number;
    setPlaybackRate(rate: number): void;
    // Group mute and volume commands land here.
    mute(): void;
    unmute(): void;
    /** 0 silent, 1 loudest. The player's own scale is this hook's problem. */
    setVolume(volume: number): void;
    /**
     * Loads `url` as a stitched reel if it is not already the loaded one, seeks
     * to `offsetMs` into it, and plays. Resolves true when the player accepted
     * it, false when there is no player to hand it to.
     */
    playReel(url: string, offsetMs: number): Promise<boolean>;
    /** Puts the game back and resumes where the fan left it. */
    restoreGame(): Promise<void>;
    /**
     * The frame on screen right now as a small JPEG data url, or null when
     * there is none to read. This is what a new clip's thumbnail is made of.
     */
    captureFrame(): string | null;
  };
  /**
   * Renews this app's session and hands back the new token, or null when it
   * cannot. The layer calls its own API with our token and has no session of
   * its own, so an expired one is ours to fix.
   */
  refreshAuth(): Promise<string | null>;
  /**
   * The clip buttons under the player, reported rather than acted on.
   *
   * The buttons stay where they have always been; the Audience Layer performs
   * the write. That split is deliberate: this page used to POST the clip into
   * the legacy store itself, and a layer that also created one would produce
   * two records of one clip.
   */
  clipRequests: {
    /** Returns the function that stops listening. */
    subscribe(handler: (type: string) => void): () => void;
  };
}

/** Bitmovin's player, as far as this hook uses it. */
type PlayerLike =
  | {
      getCurrentTime?: (mode?: string) => number;
      isPlaying?: () => boolean;
      isStalled?: () => boolean;
      play?: () => void;
      pause?: () => void;
      seek?: (time: number) => void;
      isLive?: () => boolean;
      getMaxTimeShift?: () => number;
      timeShift?: (time: number) => void;
      getPlaybackSpeed?: () => number;
      setPlaybackSpeed?: (speed: number) => void;
      mute?: () => void;
      unmute?: () => void;
      setVolume?: (volume: number) => void;
      // The reel's own control surface: what is loaded, loading something else
      // in its place, and the events a one-shot "reel finished" listener needs.
      getSource?: () => unknown;
      load?: (source: unknown) => Promise<void>;
      on?: (event: string, handler: () => void) => void;
      off?: (event: string, handler: () => void) => void;
      // The element actually decoding the game, which is where a clip's
      // thumbnail is read from.
      getVideoElement?: () => HTMLVideoElement | null;
    }
  | null
  | undefined;

interface Params {
  gameId: string;
  tenant: string;
  /** Where this tenant's auth lives, for renewing an expired session. */
  authApiUrl: string;
  apiBaseUrl?: string;
  playerRef: { current: PlayerLike };
  /**
   * The page's own opinion of whether this game is live, alongside the
   * player's. jumpToTime decides live from `playerRef.current.isLive() ||
   * gameData?.is_live`, because the player can lag behind what the page
   * already knows: a game that has gone live does not make isLive() true at
   * the same instant. This hook has to agree with both halves of that check,
   * not just the player's.
   *
   * Optional and false by default, so a caller that does not pass it (the
   * standalone harness, any test that predates this) behaves exactly as
   * before: live only when the player itself says so.
   */
  isLive?: boolean;
  /**
   * The fan's plan, for the download check below. Threaded in rather than
   * read here directly: `VideoPlayerLive` already receives it as a prop
   * (`subscriptionDetails`) and this hook has no other route to it, unlike
   * the permissions half of the same check, which travel inside the access
   * token this hook already reads.
   */
  subscription?: { plan_type_id?: number | string | null } | null;
  /**
   * Passed straight through to the context above. Read from the url by the
   * page, because the page owns the url and this hook owns the contract.
   */
  clipTarget?: { momentId: string; tab: "mine" | "shared" | null } | null;
}

/**
 * Builds the context the Audience Layer needs from what the Watch page already
 * knows: who is signed in, which game is on screen, and where playback is.
 *
 * The result is memoized on the values the remote actually keys off. It must
 * be: the remote memoizes its API clients on the context object's identity, so
 * a new object every render would rebuild them and re-fire their effects.
 */
export function useAudienceLayerHost({
  gameId,
  tenant,
  authApiUrl,
  apiBaseUrl,
  playerRef,
  isLive = false,
  subscription,
  clipTarget = null,
}: Params): {
  context: AudienceLayerHostContext | null;
  emitClipRequest: (type: string) => void;
} {
  const auth = getCurrentAuth();
  const accessToken = auth?.AccessToken;
  const userId = auth?.UserID;

  // The getters are read on every message the fan sends, long after this hook
  // ran, so they close over a ref rather than over a rendered value. Keeping
  // the ref in a ref of our own keeps the returned object stable even if the
  // player is swapped out (theatre mode remounts it).
  const player = useRef(playerRef);
  player.current = playerRef;

  /*
   * Held in a ref, not rebuilt per render.
   *
   * The layer subscribes when the context's identity changes, so an emitter
   * that was a new object each time would leave the layer holding a
   * subscription to a set nothing publishes into.
   */
  const clipListeners = useRef(new Set<(type: string) => void>());

  const clipRequests = useRef({
    subscribe(handler: (type: string) => void) {
      clipListeners.current.add(handler);
      return () => { clipListeners.current.delete(handler); };
    },
  });

  const emitClipRequest = useCallback((type: string) => {
    for (const listener of clipListeners.current) listener(type);
  }, []);

  /*
   * What the game was playing before a highlight took the player over, and which
   * reel is loaded now.
   *
   * A ref rather than state: nothing renders off it, and putting it in state
   * would rebuild the context object, which the remote memoizes its API clients
   * on.
   */
  const reel = useRef<{
    loadedUrl: string | null;
    gameSource: unknown | null;
    gamePositionSec: number;
    gameWasLive: boolean;
  }>({ loadedUrl: null, gameSource: null, gamePositionSec: 0, gameWasLive: false });

  /*
   * Back to the game, at the point the fan left it.
   *
   * A live game goes back to the live edge rather than to the second they were
   * at: a highlight takes time to watch, and resuming behind the play would put
   * them further behind than they were.
   */
  const restoreGame = useCallback(async (): Promise<void> => {
    const p = player.current?.current;
    // Nothing was ever taken over, so there is nothing to put back. Reloading
    // the game here would interrupt a fan who is simply watching it.
    if (!p || reel.current.loadedUrl === null) return;

    const { gameSource, gamePositionSec, gameWasLive } = reel.current;
    reel.current.loadedUrl = null;

    if (gameSource === null) return;
    await p.load?.(gameSource);

    if (gameWasLive) {
      // 0 is the live edge in Bitmovin's timeShift scale.
      p.timeShift?.(0);
    } else {
      p.seek?.(gamePositionSec);
    }
    p.play?.();
  }, []);

  /*
   * Whether this fan may download, decided here because only the host knows
   * their plan. The widget renders the control and the upgrade dialog off this
   * one boolean and never learns what a plan is.
   *
   * The rule itself now lives in src/utils/audienceLayerEntitlement.ts, not
   * here: the Studio page (v2) needs the same check and cannot import from
   * src/Pages, so it moved to where both halves of the app can reach it. See
   * that module for why it stays a courtesy rather than the gate.
   */
  const canDownload = useMemo(
    () => canDownloadFrom({ token: accessToken, subscription }),
    // subscription?.plan_type_id rather than subscription itself:
    // canDownloadFrom only ever reads that one field, and a caller handing
    // over a fresh subscription object each render would otherwise re-run
    // this decode and JSON parse of the access token every render for no
    // change in the result.
    [accessToken, subscription?.plan_type_id],
  );

  const context = useMemo(() => {
    // Signed out, so there is nobody to attribute messages or reactions to.
    // Returning null lets the caller render nothing rather than mount a panel
    // that would 401 on its first request.
    if (!accessToken || userId === undefined || userId === null || userId === "") return null;

    const name = [auth?.FirstName, auth?.LastName].filter(Boolean).join(" ").trim();

    /*
     * Where playback is, in the game's own terms. Live, on demand and inside a
     * reel alike, this is what getCurrentTime("relativetime") already reports,
     * and there is deliberately no branch here.
     *
     * This function used to correct for the live edge, and that correction was
     * the league's wrong-time bug. The reasoning behind it was that Bitmovin
     * counts live playback backwards from the live edge, so the position in
     * the game was the distance from the far end of the time-shift window,
     * `seconds - getMaxTimeShift()`. The premise is false. It is getTimeShift()
     * that is negative at the live edge; TimeMode.RelativeTime is documented as
     * defining "the beginning of the DVR window at the time of tuning in" as 0,
     * so it already counts forward from the start of the broadcast.
     *
     * The correction therefore added the window on top of a number that had
     * already measured it. Our streams are #EXT-X-PLAYLIST-TYPE:EVENT, whose
     * window reaches back to the game's first segment and only grows, so at the
     * live edge getMaxTimeShift() is the exact negative of the position and the
     * result was precisely double. One fan's Save at 1:24:31 was stored at
     * 2:49:03, past the end of the asset, and the cut clamped to zero and
     * returned the broadcast from the top.
     *
     * The legacy page is the reference: handleClipPost in index.tsx has cut
     * clips from `getCurrentTime("relativetime") - 5` since 2023 with no window
     * arithmetic, and skipForward reads the distance to the live edge as
     * `-position - maxTimeShift`, which is only the right way round if position
     * counts forward. seekMs below is the other half of the round trip and was
     * already correct, so this reading is what had to move to meet it.
     *
     * Clamped at zero because start_ms is unsigned at the API, and a player
     * that reports a small negative while it settles should stamp the start of
     * the game rather than be rejected.
     */
    const positionMs = () => {
      const p = player.current?.current;
      if (!p) return 0;

      const seconds = p.getCurrentTime?.("relativetime");
      if (!Number.isFinite(seconds)) return 0;

      return Math.max(0, Math.round((seconds as number) * 1000));
    };

    return {
      gameId,
      tenant,
      apiBaseUrl,
      device: "web",
      canDownload,
      /*
       * The layer's messages go through this app's own toasts, so a fan sees
       * one notification style across a page they read as one product. The
       * layer says what; this says where.
       */
      notify: (message: string, kind: "info" | "error" = "info") => {
        if (kind === "error") toast.error(message);
        else toast.info(message);
      },
      user: {
        id: String(userId),
        // Falling back to the email's local part rather than to "Anonymous":
        // a name is what other fans see next to every message.
        name: name || auth?.Email?.split("@")[0] || `Fan ${userId}`,
        jwt: accessToken,
      },
      getPlaybackPositionMs: positionMs,

      /*
       * One refresh path for the whole page. This is the same single-flight
       * call the app's own axios interceptor uses, so a burst of 401s from the
       * layer and the app together still makes one request, and both end up
       * holding the same new token.
       */
      refreshAuth: () => refreshAuthToken(authApiUrl),

      /*
       * The watch party's end of the player. Every method reaches through the
       * ref at call time rather than closing over a player instance, because
       * theatre mode remounts the player and a captured instance would go on
       * driving a video nobody is watching.
       *
       * Each is guarded: Bitmovin is loaded asynchronously and swapped between
       * implementations on this page, so a control can genuinely be absent for
       * a moment, and a party member's seek arriving in that window should do
       * nothing rather than throw inside the remote.
       */
      player: {
        getPositionMs: positionMs,
        isPlaying: () => player.current?.current?.isPlaying?.() === true,
        isStalled: () => player.current?.current?.isStalled?.() === true,
        play: () => { player.current?.current?.play?.(); },
        pause: () => { player.current?.current?.pause?.(); },
        /*
         * Seconds at the player, milliseconds in the contract, and two
         * different calls depending on whether this is live.
         *
         * seek() on a live stream does not do what a Moment needs: the timeline
         * is anchored to the live edge, so the same call has to become a
         * timeShift measured from the far end of the window. This is the same
         * branch the legacy page's jumpToTime has always taken.
         */
        seekMs: (ms: number) => {
          const p = player.current?.current;
          if (!p) return;

          if (p.isLive?.() === true || isLive) {
            const maxShift = p.getMaxTimeShift?.() ?? 0;
            p.timeShift?.(maxShift + ms / 1000);
            return;
          }

          p.seek?.(ms / 1000);
        },
        /*
         * The frame on screen right now, small, as a JPEG data url.
         *
         * This is a clip's thumbnail. The fan is watching the play as they
         * press the button, so the player has already decoded exactly the
         * frame they mean: reading it back costs one canvas and no network at
         * all, where generating it afterwards would mean seeking a three hour
         * stream server-side to a moment nobody is watching any more.
         *
         * Sized down here rather than anywhere later. The tile is 112 pixels
         * wide, so 320 covers it on any display worth the pixels, and it keeps
         * the create request to a few tens of kilobytes.
         */
        captureFrame: () => {
          const video = player.current?.current?.getVideoElement?.();
          // HAVE_CURRENT_DATA. Anything below it means there is no frame to
          // read yet, and drawing would paint an empty rectangle.
          if (!video || video.readyState < 2) return null;

          const { videoWidth: width, videoHeight: height } = video;
          if (width === 0 || height === 0) return null;

          try {
            const canvas = document.createElement("canvas");
            canvas.width = THUMBNAIL_WIDTH;
            canvas.height = Math.round((THUMBNAIL_WIDTH * height) / width);
            const ctx = canvas.getContext("2d");
            if (!ctx) return null;

            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            return canvas.toDataURL("image/jpeg", 0.6);
          } catch {
            /*
             * A canvas that has drawn cross-origin video is tainted, and
             * toDataURL throws SecurityError rather than returning anything.
             * Whether that happens depends on how the browser is playing the
             * stream: through MSE the media is fetched by script and the
             * element is not cross-origin, while a native HLS playback can
             * taint it.
             *
             * So this is a real possibility on some browsers and not an error
             * in any of them: no thumbnail, the same as a fan clipping before
             * the first frame decodes, and the card shows its type glyph.
             */
            return null;
          }
        },

        // Bitmovin calls it speed; the contract calls it rate, because that is
        // what the sync SDK calls it. 1 when the player cannot say, which
        // reads as "normal" rather than as a rate of zero.
        getPlaybackRate: () => player.current?.current?.getPlaybackSpeed?.() ?? 1,
        setPlaybackRate: (rate: number) => { player.current?.current?.setPlaybackSpeed?.(rate); },
        mute: () => { player.current?.current?.mute?.(); },
        unmute: () => { player.current?.current?.unmute?.(); },
        // The contract is 0 to 1 because that is what the sync SDK sends.
        // Bitmovin's own scale is 0 to 100, and this is the only place that
        // difference should ever be visible.
        setVolume: (volume: number) => {
          const clamped = Math.min(1, Math.max(0, volume));
          player.current?.current?.setVolume?.(Math.round(clamped * 100));
        },

        /*
         * A highlight in the page's own player, as one stitched reel.
         *
         * The reel is loaded once and every later press is a seek into it, which is
         * what the legacy Highlights tab did and why it felt instant: a reload costs a
         * couple of seconds and restarts the video.
         *
         * Deliberately NOT routed through seekMs. That call branches on
         * `p.isLive() || isLive` and turns a seek into a timeShift from the live edge.
         * A reel is a VOD playlist, so the player reports VOD, but the page's own
         * isLive flag is still true while the game runs: the branch would fire on the
         * page's half of the condition and seek nowhere. A reel's timeline starts at
         * zero and a plain seek is always right for it.
         */
        playReel: async (url: string, offsetMs: number): Promise<boolean> => {
          const p = player.current?.current;
          if (!p) return false;

          if (reel.current.loadedUrl !== url) {
            // Remembered before the load, because after it the player is holding the
            // reel and can no longer say what the game was.
            if (reel.current.loadedUrl === null) {
              reel.current.gameSource = p.getSource?.() ?? null;
              reel.current.gameWasLive = p.isLive?.() === true || isLive;
              const seconds = p.getCurrentTime?.("relativetime");
              reel.current.gamePositionSec = Number.isFinite(seconds) ? (seconds as number) : 0;
            }

            // One shot: the reel returns the fan to the game when it runs out, and a
            // listener left attached would fire again on the game's own end.
            // "playbackfinished" is PlayerEvent.PlaybackFinished, written as its value
            // so this hook needs no bitmovin import and its tests can drive a plain
            // object.
            const onFinished = () => {
              p.off?.("playbackfinished", onFinished);
              void restoreGame();
            };
            p.on?.("playbackfinished", onFinished);

            await p.load?.({ hls: url });
            reel.current.loadedUrl = url;
          }

          p.seek?.(offsetMs / 1000);
          p.play?.();
          return true;
        },

        /*
         * Back to the game, at the point the fan left it.
         *
         * A live game goes back to the live edge rather than to the second they were
         * at: a highlight takes time to watch, and resuming behind the play would put
         * them further behind than they were.
         */
        restoreGame,
      },
      // No game clock source on this page yet. Null is part of the contract
      // and reads as "unknown", which is true, rather than 0:00, which is a
      // period start.
      getGameClock: () => null,
      clipRequests: clipRequests.current,
      clipTarget,
    };
    // auth is re-read from localStorage on every render and is a fresh object
    // each time, so the primitives it yields are the dependencies, not it.
    // clipRequests.current is a ref and is stable, so listing it would be
    // misleading. isLive is the boolean the caller passed, not the gameData
    // object it was read from: gameData can be a new object every render, and
    // depending on it would rebuild this context (and every API client the
    // remote memoizes off its identity) far more often than the one bit that
    // actually matters ever changes.
  }, [gameId, tenant, apiBaseUrl, authApiUrl, accessToken, userId, auth?.FirstName, auth?.LastName, auth?.Email, isLive, restoreGame, canDownload, clipTarget]);

  return { context, emitClipRequest };
}
